import React, { useMemo } from "react";
import CustomerDashboard from "./Dashboard/CustomerDashboard";
import CustomerUserDashboard from "./Dashboard/CustomerUserDashboard";
import MyTasksDashboard from "./Dashboard/MyTasksDashboard";

const safeParseJson = (value) => {
  if (!value) return null;
  try { return JSON.parse(value); } catch { return null; }
};

const getRoleNames = (user) => {
  if (!user) return [];
  const fromArray = Array.isArray(user.roleNames) ? user.roleNames
    : Array.isArray(user.RoleNames) ? user.RoleNames : [];
  if (fromArray.length) return fromArray.map((item) => String(item || "").trim()).filter(Boolean);
  const single = user?.roleName ?? user?.role ?? user?.RoleName ?? user?.Role ?? "";
  return single ? [String(single).trim()] : [];
};

const normalizeRoleText = (value) =>
  String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

const ROLE_SECTIONS = {
  support_manager: { label: "Support Manager", title: "Support Manager Section" },
  support: { label: "Support", title: "Support Section" },
  employee: { label: "Employee", title: "Employee Section" },
  customer: { label: "Customer", title: "Customer Section" },
  customer_user: { label: "Customer User", title: "Customer User Section" },
  developer: { label: "Developer", title: "Developer Section" },
  admin: { label: "Admin", title: "Admin Section" },
  user: { label: "User", title: "User Section" },
  ceo: { label: "CEO", title: "CEO Section" },
};

const ROLE_PRIORITY = [
  { key: "support_manager", match: (role) => role === "support manager" },
  { key: "customer_user", match: (role) => role === "customer user" },
  { key: "developer", match: (role) => role === "developer" },
  { key: "admin", match: (role) => role === "admin" },
  { key: "support", match: (role) => role === "support" },
  { key: "employee", match: (role) => role === "employee" },
  { key: "customer", match: (role) => role === "customer" },
  { key: "user", match: (role) => role === "user" },
  { key: "ceo", match: (role) => role === "ceo" },
];

const resolveRoleSection = (rawRoles) => {
  const normalizedRoles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
    .map(normalizeRoleText).filter(Boolean);
  for (const roleRule of ROLE_PRIORITY) {
    if (normalizedRoles.some(roleRule.match)) return { key: roleRule.key, ...ROLE_SECTIONS[roleRule.key] };
  }
  return { key: "user", ...ROLE_SECTIONS.user };
};

const getDisplayName = (user) => {
  const name = user?.fullName ?? user?.FullName ?? user?.name ?? user?.userName
    ?? user?.username ?? user?.email ?? user?.Email ?? "User";
  return String(name).trim() || "User";
};

const BRAND_GRADIENT = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const Dashboard = () => {
  const user = useMemo(() => safeParseJson(localStorage.getItem("user")), []);
  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const roleInfo = useMemo(() => resolveRoleSection(roleNames), [roleNames]);
  const displayName = useMemo(() => getDisplayName(user), [user]);

  const today = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" }).format(new Date());
    } catch { return new Date().toDateString(); }
  }, []);

  if (roleInfo.key === "customer") {
    return <CustomerDashboard displayName={displayName} />;
  }

  if (roleInfo.key === "customer_user") {
    return <CustomerUserDashboard displayName={displayName} />;
  }

  // All other roles (admin, hr, sales, support, support_manager, employee, developer, user, manager)
  // show My Tasks dashboard — tasks are always scoped to the logged-in user by userId
  if (roleInfo.key !== "admin" && roleInfo.key !== "ceo") {
    return <MyTasksDashboard displayName={displayName} />;
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: BRAND_GRADIENT }}>
        <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/5 blur-3xl" />
        <div className="pointer-events-none absolute -left-6 bottom-0 h-28 w-28 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.10)" }} />
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />
        <div className="relative flex flex-col gap-3 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-extrabold tracking-tight text-white drop-shadow-sm">Dashboard</h1>
            <p className="mt-1 text-sm text-orange-100">
              Welcome back, <span className="font-semibold text-white">{displayName}</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-orange-100 backdrop-blur-sm">
              {roleInfo.label}
            </span>
            <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-orange-100 backdrop-blur-sm">
              {today}
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700 dark:text-primary-200">
          {roleInfo.label}
        </div>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{roleInfo.title}</h2>
      </div>
    </div>
  );
};

export default Dashboard;
